import "./styles/index.css";

import React from "react";
import { createRoot } from "react-dom/client";
import { App } from "./app/App";
import { useAuth } from "./app/auth/hooks";
import { AuthConfigError } from "./app/components/AuthConfigError";
import { initFaro } from "./observability/faro";
import {
  shareMsalWithFeedbackHub,
  installFeedbackHubTokenProvider,
} from "./app/auth/feedbackHubToken";
import {
  isAuthPopupReturn,
  relayAuthPopupReturn,
} from "./app/auth/popupReturn";
import { installConsoleErrorHook } from "./platform/feedback-capture";
import {
  loadRuntimeConfig,
  getRuntimeConfig,
} from "@shared-core/config/runtime-config";
import { decodePortalViewReturnPath } from "@shared-core/domain/staff-view-return-path";

import type { DataAdapter } from "@shared-core/data/adapter";
import type { ObjectUi } from "./app/App";

// INVARIANT: nothing in this file's *static* import graph may read
// configuration. The SPA ships as one environment-agnostic artifact and
// resolves its configuration from /config.json at boot, so any module-level
// config read would evaluate before loadRuntimeConfig() has resolved.
// msalConfig is the module that trips this — it is reached by dynamic
// import() inside boot() below, never statically. initFaro() and the API
// modules are safe because they read inside functions.

const container = document.getElementById("root")!;
const root = createRoot(container);

function AppWithAdapter({
  adapter,
  objectUi,
}: {
  adapter: DataAdapter;
  /** One's adapter and manifest; the live ones unless mock mode gives its own. */
  objectUi?: ObjectUi;
}) {
  const authValue = useAuth();
  return (
    <App
      dataAdapter={adapter}
      authValue={authValue}
      {...(objectUi ? { objectUi } : {})}
    />
  );
}

function renderBootstrapError(
  title: string,
  description: string,
  detail: string,
) {
  root.render(
    <React.StrictMode>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          padding: 24,
          fontFamily: "system-ui, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: 640,
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            padding: 24,
          }}
        >
          <h2 style={{ marginTop: 0 }}>{title}</h2>
          <p>{description}</p>
          <pre
            style={{
              background: "#f5f5f5",
              padding: 12,
              borderRadius: 4,
              overflowX: "auto",
              fontSize: 12,
            }}
          >
            {detail}
          </pre>
        </div>
      </div>
    </React.StrictMode>,
  );
}

async function boot() {
  // A sign-in popup (the feedback hub's, on a preview) returns to this page: relay its response to the window that
  // opened it and stop, rendering nothing.
  if (isAuthPopupReturn()) {
    await relayAuthPopupReturn();
    return;
  }

  // Configuration first — everything below reads it.
  await loadRuntimeConfig();

  // Console errors are remembered from here on, for the feedback panel's "recent errors".
  installConsoleErrorHook();
  const { feedbackHubApiBaseUrl, feedbackHubScope } = getRuntimeConfig();
  if (feedbackHubApiBaseUrl && feedbackHubScope) {
    installFeedbackHubTokenProvider(feedbackHubScope);
  }

  // Grafana Faro. As early as possible after config so the earliest errors in
  // the auth / adapter chain are captured. Wrapped in try/catch — a broken
  // observability stack must never take down the app.
  try {
    initFaro();
  } catch (err) {
    console.error("[faro] init failed:", err);
  }

  const hasApiUrl = Boolean(getRuntimeConfig().apiBaseUrl);

  // Dynamic, never static: msalConfig reads configuration at module scope.
  const {
    isDevBypass,
    isEntraMisconfigured,
    getEntraMisconfigurationDetails,
    getMsalConfig,
  } = await import("./app/auth/entra/msalConfig");

  if (isEntraMisconfigured) {
    root.render(
      <React.StrictMode>
        <AuthConfigError
          missingConfigKeys={getEntraMisconfigurationDetails()}
        />
      </React.StrictMode>,
    );
    return;
  }

  if (isDevBypass && hasApiUrl) {
    // Dev bypass with real API — no MSAL, no token, real data
    Promise.all([
      import("./app/auth/dev_bypass/DevBypassAuthProvider"),
      import("@shared-core/data/api/real-adapter"),
    ]).then(([{ DevBypassAuthProvider }, { createRealDataAdapter }]) => {
      const adapter = createRealDataAdapter();
      root.render(
        <React.StrictMode>
          <DevBypassAuthProvider>
            <AppWithAdapter adapter={adapter} />
          </DevBypassAuthProvider>
        </React.StrictMode>,
      );
    });
    return;
  }

  if (!isDevBypass && hasApiUrl) {
    // Entra auth with real API.
    // msal-browser v3+ requires explicit initialize() before use, and
    // handleRedirectPromise() must run before any other MSAL call so that
    // a returning redirect (#code=...) is consumed and an account is cached.
    Promise.all([
      import("@azure/msal-react"),
      import("@azure/msal-browser"),
      import("./app/auth/entra/MsalAuthProvider"),
      import("@shared-core/data/api/real-adapter"),
    ])
      .then(
        async ([
          { MsalProvider },
          { PublicClientApplication },
          { MsalAuthProvider },
          { createRealDataAdapter },
        ]) => {
          const msalInstance = new PublicClientApplication(getMsalConfig());
          try {
            await msalInstance.initialize();
            const redirectResult = await msalInstance.handleRedirectPromise();
            // A staff-view deep link (/portal-view/...) that went through an
            // interactive sign-in redirect lands here — the registered Entra
            // redirect URI is this app's root, not /portal-view/... (see
            // staff-view-return-path.ts). Forward to the original location
            // the user opened rather than rendering the staff dashboard.
            const returnPath = decodePortalViewReturnPath(
              redirectResult?.state,
            );
            if (returnPath) {
              window.location.replace(returnPath);
              return;
            }
          } catch (err) {
            // Bootstrap failure: Entra returned an error in the redirect (e.g. AADSTS50011
            // unregistered redirect URI, AADSTS65001 consent required) or MSAL itself failed
            // to initialize. Render a visible error rather than silently hanging on a blank page.
            console.error("[auth] MSAL bootstrap failed:", err);
            const message = err instanceof Error ? err.message : String(err);
            renderBootstrapError(
              "Authentication bootstrap failed",
              "MSAL could not initialize or process the redirect from Entra. " +
                "This usually means the redirect URI is not registered, consent is required, " +
                "or the SPA app registration is misconfigured.",
              message,
            );
            return;
          }
          shareMsalWithFeedbackHub(msalInstance);
          const adapter = createRealDataAdapter();
          root.render(
            <React.StrictMode>
              <MsalProvider instance={msalInstance}>
                <MsalAuthProvider>
                  <AppWithAdapter adapter={adapter} />
                </MsalAuthProvider>
              </MsalProvider>
            </React.StrictMode>,
          );
        },
      )
      .catch((err) => {
        console.error("[auth] Entra bootstrap chain failed:", err);
        renderBootstrapError(
          "Failed to load auth modules",
          "A dynamic import or render step failed before MSAL could start.",
          err instanceof Error ? err.message : String(err),
        );
      });
    return;
  }

  // Mock mode — no API, hardcoded mock user + data
  Promise.all([
    import("./app/auth/dev_bypass/MockAuthProvider"),
    import("@shared-core/mock-data/mock-adapter"),
    import("@shared-core/mock-data/customer-portal-access"),
    import("@shared-core/mock-data/object-ui-adapter"),
    import("@shared-core/mock-data/object-ui-manifest"),
    import("@shared-core/mock-data/object-ui-drafts/kit"),
    import("@shared-core/mock-data/object-ui-drafts"),
  ]).then(
    ([
      { MockAuthProvider },
      { mockAdapter },
      { parseMockPortalStaffRole, setMockPortalStaffRole },
      { mockObjectUiAdapter },
      { mockObjectManifest },
      { withDraftManifest, withDrafts },
      { DRAFTS },
    ]) => {
      // Proposed objects (object-ui-drafts) shown in One's real screens before any module serves them.
      // VITE_OBJECT_UI_DRAFTS=off (Playwright's build) serves only what modules serve.
      const drafts = getRuntimeConfig().objectUiDrafts === "off" ? [] : DRAFTS;
      const manifest = withDraftManifest(mockObjectManifest, drafts);
      // Review only: `?as=csr` (manager, sales_rep, other) on first load shows
      // what each role is offered. The real server works it out from permits.
      setMockPortalStaffRole(
        parseMockPortalStaffRole(
          new URLSearchParams(window.location.search).get("as"),
        ),
      );
      root.render(
        <React.StrictMode>
          <MockAuthProvider>
            <AppWithAdapter
              adapter={mockAdapter}
              objectUi={{
                adapter: withDrafts(mockObjectUiAdapter, manifest, drafts),
                manifest,
                // The record-layout mock-up (docs/features/one-record-layout/01_mock_up.md): proposed in the mock
                // app with drafts, switchable from the account menu; Playwright's build (no drafts) draws today's.
                recordLayout:
                  drafts.length > 0
                    ? { fallback: "tabs", isSwitchOffered: true }
                    : { fallback: "today", isSwitchOffered: false },
              }}
            />
          </MockAuthProvider>
        </React.StrictMode>,
      );
    },
  );
}

boot().catch((err) => {
  console.error("[boot] application bootstrap failed:", err);
  renderBootstrapError(
    "Application failed to start",
    "The app could not resolve its runtime configuration or load its auth modules.",
    err instanceof Error ? err.message : String(err),
  );
});
